/**
 * Form state for /ivt/radar-config.
 *
 * The page shows one GENERAL_TOPIC block + up to 10 STANDARD_TOPIC blocks
 * + a NEWS_SOURCE table, with a single Confirm button that commits all
 * changes against the Admin ops. The service has no batch op — one op per
 * row — so Confirm fans out into N granular writes.
 *
 * This module keeps the "what did the user actually change" bookkeeping
 * out of the component: it builds a submit plan from the loaded snapshot
 * + the edited draft, so the component is a view and the tests pin the
 * mapping rule-by-rule.
 *
 * News sources are NOT part of the Confirm batch. The Figma shows per-row
 * delete 🗑 and a +新增资源 button; this matches the service's granular
 * ops better than batching keyword edits does, and gives the STO one less
 * place a Confirm-click can lose work. Row edits are therefore fired
 * immediately against the client, not through this plan.
 */

import type {
  RadarStandardTopic,
  RadarGeneralTopic,
  RadarStandardTopicDraft,
  RadarGeneralTopicDraft,
} from '../../client/IvtHubClient';

export const MAX_ACTIVE_TOPICS = 10;
/**
 * Per-field keyword cap (search / exclude on both STANDARD_TOPIC and
 * GENERAL_TOPIC). Mirrors the service's advertised 128 limit so the user
 * sees inline feedback before Confirm, and so other valid edits in the
 * same batch do not get aborted by a late service rejection.
 */
export const MAX_KEYWORDS_PER_FIELD = 128;

/** One STANDARD_TOPIC form block. `topicId=null` = created in this session. */
export interface TopicDraftRow {
  /** Stable id for keying the React list — distinct from topicId since new rows have none. */
  localKey: string;
  /** `null` for a row the user added this session; the service id otherwise. */
  topicId: string | null;
  title: string;
  extractionRule: string;
  inductionRule: string;
  searchKeywords: string[];
  excludeKeywords: string[];
}

/** GENERAL_TOPIC form state. Mirrors `TopicDraftRow` minus title/order (fixed by seed). */
export interface GeneralDraft {
  searchKeywords: string[];
  excludeKeywords: string[];
  extractionRule: string;
  inductionRule: string;
}

export interface RadarConfigFormState {
  general: GeneralDraft | null;
  topics: TopicDraftRow[];
  /** topicId → "remove on Confirm". New rows (topicId=null) just leave the array. */
  topicsToRemove: Set<string>;
}

/** Build the initial editable state from a loaded snapshot. */
export function initialFormState(
  general: RadarGeneralTopic | null,
  topics: readonly RadarStandardTopic[],
): RadarConfigFormState {
  return {
    general: general
      ? {
          searchKeywords: general.searchKeywords ?? [],
          excludeKeywords: general.excludeKeywords ?? [],
          extractionRule: general.extractionRule ?? '',
          inductionRule: general.inductionRule ?? '',
        }
      : null,
    topics: topics.map((t, i) => ({
      localKey: t.topicId ?? `loaded-${i}`,
      topicId: t.topicId ?? null,
      title: t.title ?? '',
      extractionRule: t.extractionRule ?? '',
      inductionRule: t.inductionRule ?? '',
      searchKeywords: t.searchKeywords ?? [],
      excludeKeywords: t.excludeKeywords ?? [],
    })),
    topicsToRemove: new Set(),
  };
}

/** A single mutation Confirm will fire against the service. */
export type SubmitStep =
  | {
      kind: 'upsertGeneral';
      draft: RadarGeneralTopicDraft;
    }
  | {
      /** Create: `topicId` absent on the service call. */
      kind: 'createTopic';
      /** Carries the UI's localKey so the hook can swap in the new topicId. */
      localKey: string;
      draft: RadarStandardTopicDraft;
    }
  | {
      kind: 'updateTopic';
      topicId: string;
      draft: RadarStandardTopicDraft;
    }
  | {
      kind: 'removeTopic';
      topicId: string;
    };

export interface SubmitPlan {
  steps: SubmitStep[];
  /** Pre-flight errors — if any, Confirm must NOT run. */
  errors: readonly string[];
}

/** Shallow array equality for the keyword comparisons. */
function sameStringList(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Trim user text and strip control characters EXCEPT tab (\t 0x09),
 * line-feed (\n 0x0a) and carriage-return (\r 0x0d). The rule fields are
 * multi-line Textareas; preserving newlines is required or every line
 * break would be collapsed on submit and the normalized value would never
 * match the loaded one, emitting a spurious Upsert on every open.
 */
function cleanText(s: string): string {
  return s.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '').trim();
}

/** `[a, b, '', c, '  ', c]` → `[a, b, c]`. Order-preserving. */
export function normalizeKeywords(items: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const v = cleanText(raw);
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/** Diff a loaded topic against its draft; returns the Upsert payload or null if clean. */
function topicDraftDiff(
  loaded: RadarStandardTopic | undefined,
  row: TopicDraftRow,
): RadarStandardTopicDraft | null {
  const draft: RadarStandardTopicDraft = {
    title: cleanText(row.title),
    searchKeywords: normalizeKeywords(row.searchKeywords),
    excludeKeywords: normalizeKeywords(row.excludeKeywords),
    extractionRule: cleanText(row.extractionRule) || undefined,
    inductionRule: cleanText(row.inductionRule) || undefined,
  };
  if (!loaded) return draft; // new row — always a create
  // Normalize / clean BOTH sides of every comparison: the loaded row's data
  // came from the service and may carry trailing whitespace, stripped
  // control chars, or duplicates in older rows. Comparing raw loaded vs.
  // cleaned draft would emit a spurious Upsert on the first open of such
  // a row — exactly the regression the keyword comparison already guards.
  const loadedTitle = cleanText(loaded.title ?? '');
  const loadedExt = cleanText(loaded.extractionRule ?? '');
  const loadedInd = cleanText(loaded.inductionRule ?? '');
  if (
    draft.title === loadedTitle &&
    (draft.extractionRule ?? '') === loadedExt &&
    (draft.inductionRule ?? '') === loadedInd &&
    sameStringList(draft.searchKeywords ?? [], normalizeKeywords(loaded.searchKeywords ?? [])) &&
    sameStringList(draft.excludeKeywords ?? [], normalizeKeywords(loaded.excludeKeywords ?? []))
  ) {
    return null;
  }
  return draft;
}

function generalDraftDiff(
  loaded: RadarGeneralTopic | null,
  draft: GeneralDraft | null,
): RadarGeneralTopicDraft | null {
  if (!loaded || !draft) return null; // can't upsert if GENERAL has not been seeded
  const payload: RadarGeneralTopicDraft = {
    searchKeywords: normalizeKeywords(draft.searchKeywords),
    excludeKeywords: normalizeKeywords(draft.excludeKeywords),
    extractionRule: cleanText(draft.extractionRule) || undefined,
    inductionRule: cleanText(draft.inductionRule) || undefined,
  };
  const loadedExt = cleanText(loaded.extractionRule ?? '');
  const loadedInd = cleanText(loaded.inductionRule ?? '');
  if (
    (payload.extractionRule ?? '') === loadedExt &&
    (payload.inductionRule ?? '') === loadedInd &&
    sameStringList(payload.searchKeywords ?? [], normalizeKeywords(loaded.searchKeywords ?? [])) &&
    sameStringList(payload.excludeKeywords ?? [], normalizeKeywords(loaded.excludeKeywords ?? []))
  ) {
    return null;
  }
  return payload;
}

export interface PlanInputs {
  initialGeneral: RadarGeneralTopic | null;
  initialTopics: readonly RadarStandardTopic[];
  state: RadarConfigFormState;
}

/**
 * Produce the full submit plan from the current form state.
 *
 * Validation gate:
 *   - every topic in `state.topics` must have a non-empty title
 *   - every new topic (topicId=null) must have at least one search keyword
 *   - active-topic count after the batch must stay <= 10 (the service cap).
 *
 * Any error aborts the batch — a half-applied batch is strictly worse than
 * none when the service has no transaction over multiple ops.
 */
export function buildSubmitPlan(input: PlanInputs): SubmitPlan {
  const { initialGeneral, initialTopics, state } = input;
  const errors: string[] = [];
  const steps: SubmitStep[] = [];

  // 1. GENERAL — one diff → zero or one step.
  const generalPayload = generalDraftDiff(initialGeneral, state.general);
  if (generalPayload) {
    if (
      (generalPayload.searchKeywords ?? []).length > MAX_KEYWORDS_PER_FIELD ||
      (generalPayload.excludeKeywords ?? []).length > MAX_KEYWORDS_PER_FIELD
    ) {
      errors.push(`每个关键词字段最多 ${MAX_KEYWORDS_PER_FIELD} 个`);
    } else {
      steps.push({ kind: 'upsertGeneral', draft: generalPayload });
    }
  }

  // 2. Topic removals — fire SetStatus=INACTIVE for every id staged for removal
  // that is still in the loaded set (ignore ids that no longer exist, same
  // treatment as a double-remove click).
  const loadedById = new Map(initialTopics.map((t) => [t.topicId ?? '', t]));
  for (const topicId of state.topicsToRemove) {
    if (loadedById.has(topicId)) steps.push({ kind: 'removeTopic', topicId });
  }

  // 3. Topic upserts. Validate only rows the user actually produced a
  // diff on (new rows, or loaded rows whose edited state differs from the
  // seed). A pre-existing loaded topic carrying legacy-invalid data must
  // not block an unrelated edit — the handler owns its own data-quality
  // story for untouched rows.
  //
  // An entirely-blank new row (no title + no keywords + no rules) is
  // treated as abandoned: it drops out of the batch silently, matching
  // the no-op treatment of untouched loaded rows. Without this, a user
  // who clicked 增加新主题 and then left the block empty would be stuck
  // behind a validation error that locks every other edit in the batch.
  for (const row of state.topics) {
    const isNew = row.topicId === null;
    if (isNew && isBlankNewTopic(row)) continue; // abandoned blank — ignore
    const loaded = isNew ? undefined : loadedById.get(row.topicId!);
    const diff = topicDraftDiff(loaded, row);
    if (diff === null) continue; // untouched loaded row — skip entirely
    if (!cleanText(row.title)) {
      errors.push('主题标题不能为空');
      continue;
    }
    // Require >= 1 search keyword for any row the user is submitting: an
    // empty keyword list breaks the backend's query generator (topic-model
    // section 4.1), and a user clearing a loaded topic's chips should see
    // the same validation that blocked the create.
    const searchCount = normalizeKeywords(row.searchKeywords).length;
    const excludeCount = normalizeKeywords(row.excludeKeywords).length;
    if (searchCount === 0) {
      errors.push('主题至少需要一个搜索关键词');
      continue;
    }
    if (searchCount > MAX_KEYWORDS_PER_FIELD || excludeCount > MAX_KEYWORDS_PER_FIELD) {
      errors.push(`每个关键词字段最多 ${MAX_KEYWORDS_PER_FIELD} 个`);
      continue;
    }
    if (isNew) {
      steps.push({ kind: 'createTopic', localKey: row.localKey, draft: diff });
    } else {
      steps.push({ kind: 'updateTopic', topicId: row.topicId!, draft: diff });
    }
  }

  // 4. Active-topic cap — count what the service would hold after this
  // batch. Shares the same helper the page uses for the Add-disabled
  // state so the UI and the submit path agree on the count.
  const projected = projectActiveTopicCount(initialTopics, state);
  if (projected > MAX_ACTIVE_TOPICS) {
    errors.push(`每个行业最多 ${MAX_ACTIVE_TOPICS} 个主题，当前将有 ${projected} 个`);
  }

  return { steps, errors };
}

/** True when a NEW topic row has no title, keywords, or rules — a user who added a block and abandoned it. */
export function isBlankNewTopic(row: TopicDraftRow): boolean {
  return (
    !cleanText(row.title) &&
    !cleanText(row.extractionRule) &&
    !cleanText(row.inductionRule) &&
    normalizeKeywords(row.searchKeywords).length === 0 &&
    normalizeKeywords(row.excludeKeywords).length === 0
  );
}

/**
 * Project how many ACTIVE topics the service would hold after Confirm.
 * Shared by the page's Add-disabled derivation and `buildSubmitPlan`'s cap
 * check so the UI and the submit path always agree on the count.
 */
export function projectActiveTopicCount(
  initialTopics: readonly RadarStandardTopic[],
  state: RadarConfigFormState,
): number {
  const kept = initialTopics.filter(
    (t) => t.topicId && !state.topicsToRemove.has(t.topicId),
  ).length;
  let added = 0;
  for (const row of state.topics) {
    if (row.topicId !== null) continue;
    if (isBlankNewTopic(row)) continue;
    added += 1;
  }
  return kept + added;
}

// Monotonic counter so an Add + Remove + Add within the same millisecond
// cannot produce two rows with the same localKey (Date.now() alone would
// collide; so would Date.now() + state.topics.length because length wraps
// to the same value after a Remove). React's reconciler mis-targets the
// edit onto the wrong row when two siblings share a key.
let _newLocalKeyCounter = 0;

/** Add a blank topic row to the form state. Returns a new state object. */
export function addEmptyTopic(state: RadarConfigFormState): RadarConfigFormState {
  _newLocalKeyCounter += 1;
  const localKey = `new-${Date.now()}-${_newLocalKeyCounter}`;
  return {
    ...state,
    topics: [
      ...state.topics,
      {
        localKey,
        topicId: null,
        title: '',
        extractionRule: '',
        inductionRule: '',
        searchKeywords: [],
        excludeKeywords: [],
      },
    ],
  };
}

/**
 * Remove a topic row. For an unsaved row (topicId=null) this just drops it;
 * for a saved row it adds the id to the removal set so Confirm fires
 * SetStatus(INACTIVE). Either way the row disappears from the form.
 */
export function removeTopic(
  state: RadarConfigFormState,
  localKey: string,
): RadarConfigFormState {
  const row = state.topics.find((r) => r.localKey === localKey);
  if (!row) return state;
  const nextTopics = state.topics.filter((r) => r.localKey !== localKey);
  if (row.topicId === null) return { ...state, topics: nextTopics };
  const nextRemove = new Set(state.topicsToRemove);
  nextRemove.add(row.topicId);
  return { ...state, topics: nextTopics, topicsToRemove: nextRemove };
}
